import { useEffect, useState } from "react";
import {
  getInfrastructureStatus, IInfrastructureStatus, IVirtualMachine, IVirtualMachineMetrics,
} from "../services/infrastructure";

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} ${units[i]}`;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} gün`);
  if (hours > 0) parts.push(`${hours} saat`);
  if (days === 0 && minutes > 0) parts.push(`${minutes} dk`);
  return parts.length > 0 ? parts.join(" ") : "< 1 dk";
}

const STATE_META = (state: string) => {
  const s = state?.toLowerCase();
  if (s === "running") return { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", icon: "bi-play-circle-fill", label: "Çalışıyor" };
  if (s === "stopped") return { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: "bi-stop-circle-fill", label: "Durduruldu" };
  return { color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: "bi-question-circle-fill", label: state };
};

function UsageBar({ percent }: { percent: number }) {
  const pct = Math.min(percent, 100);
  const color = pct > 80 ? "#dc2626" : pct > 60 ? "#d97706" : "#16a34a";
  return (
    <div className="hostinger-bar-wrap">
      <div className="hostinger-bar-track">
        <div className="hostinger-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="hostinger-bar-label" style={{ color }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

function VmCard({ vm, metrics }: { vm: IVirtualMachine; metrics?: IVirtualMachineMetrics }) {
  const sm = STATE_META(vm.state);
  const totalRamGb = vm.memoryMb / 1024;
  const totalDiskGb = vm.diskMb / 1024;

  return (
    <div className="hostinger-vm-card" style={{ borderColor: sm.border }}>
      {/* Header */}
      <div className="hostinger-vm-card__header">
        <div className="hostinger-vm-icon">
          <i className="bi bi-hdd-rack-fill"></i>
        </div>
        <div className="hostinger-vm-info">
          <div className="hostinger-vm-name">{vm.hostname}</div>
          <div className="hostinger-vm-desc">{vm.plan} · {vm.operatingSystem}</div>
        </div>
        <div className="hostinger-vm-right">
          <span className="hostinger-vm-status" style={{ background: sm.bg, color: sm.color, border: `1px solid ${sm.border}` }}>
            <i className={`bi ${sm.icon} me-1`}></i>{sm.label}
          </span>
          <span className="hostinger-vm-ip"><i className="bi bi-globe2 me-1"></i>{vm.primaryIpv4}</span>
        </div>
      </div>

      {/* Specs */}
      <div className="hostinger-vm-data">
        <div className="hostinger-data-row">
          <span className="hostinger-data-key"><i className="bi bi-cpu-fill me-1"></i>CPU</span>
          <span className="hostinger-data-val">{vm.cpus} çekirdek</span>
        </div>
        <div className="hostinger-data-row">
          <span className="hostinger-data-key"><i className="bi bi-memory me-1"></i>Bellek</span>
          <span className="hostinger-data-val">{totalRamGb.toLocaleString("tr-TR")} GB</span>
        </div>
        <div className="hostinger-data-row">
          <span className="hostinger-data-key"><i className="bi bi-device-hdd-fill me-1"></i>Disk</span>
          <span className="hostinger-data-val">{totalDiskGb.toLocaleString("tr-TR")} GB</span>
        </div>
        <div className="hostinger-data-row">
          <span className="hostinger-data-key"><i className="bi bi-calendar3 me-1"></i>Oluşturulma</span>
          <span className="hostinger-data-val">{formatDateTime(vm.createdAtUtc)}</span>
        </div>
      </div>

      {/* Metrics */}
      {metrics && (
        <div className="hostinger-vm-metrics">
          <div className="hostinger-metric-row">
            <span className="hostinger-data-key">CPU Kullanımı</span>
            <UsageBar percent={metrics.cpuUsagePercent} />
          </div>
          <div className="hostinger-metric-row">
            <span className="hostinger-data-key">RAM Kullanımı ({formatBytes(metrics.ramUsedBytes)} / {totalRamGb.toLocaleString("tr-TR")} GB)</span>
            <UsageBar percent={metrics.ramUsedPercent} />
          </div>
          <div className="hostinger-metric-row">
            <span className="hostinger-data-key">Disk Kullanımı ({formatBytes(metrics.diskUsedBytes)} / {totalDiskGb.toLocaleString("tr-TR")} GB)</span>
            <UsageBar percent={metrics.diskUsedPercent} />
          </div>
          <div className="hostinger-vm-data hostinger-vm-data--tight">
            <div className="hostinger-data-row">
              <span className="hostinger-data-key"><i className="bi bi-arrow-down-circle-fill me-1"></i>Gelen Trafik</span>
              <span className="hostinger-data-val">{formatBytes(metrics.incomingTrafficBytes)}</span>
            </div>
            <div className="hostinger-data-row">
              <span className="hostinger-data-key"><i className="bi bi-arrow-up-circle-fill me-1"></i>Giden Trafik</span>
              <span className="hostinger-data-val">{formatBytes(metrics.outgoingTrafficBytes)}</span>
            </div>
            <div className="hostinger-data-row">
              <span className="hostinger-data-key"><i className="bi bi-clock-history me-1"></i>Çalışma Süresi</span>
              <span className="hostinger-data-val">{formatUptime(metrics.uptimeSeconds)}</span>
            </div>
            <div className="hostinger-data-row">
              <span className="hostinger-data-key"><i className="bi bi-stopwatch me-1"></i>Son Ölçüm</span>
              <span className="hostinger-data-val">{formatDateTime(metrics.sampledAtUtc)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HostingerPage() {
  const [status, setStatus]   = useState<IInfrastructureStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchStatus = () => {
    setLoading(true);
    setError("");
    getInfrastructureStatus()
      .then((res) => { setStatus(res.data); setLastRefresh(new Date()); })
      .catch(() => setError("Sunucu durumu alınamadı."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatus(); }, []);

  const reachableMeta = status
    ? (status.reachable
        ? { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", icon: "bi-check-circle-fill", label: "Erişilebilir" }
        : { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: "bi-x-circle-fill", label: "Erişilemiyor" })
    : null;

  return (
    <div className="hostinger-page">

      {/* Header */}
      <div className="hostinger-header">
        <div className="hostinger-header__left">
          <div className="hostinger-header__icon">
            <i className="bi bi-hdd-network-fill"></i>
          </div>
          <div>
            <h4 className="hostinger-header__title">Hostinger Yönetimi</h4>
            <p className="hostinger-header__subtitle">VPS sunucu durumu</p>
          </div>
        </div>
        <button className="hostinger-refresh-btn" onClick={fetchStatus} disabled={loading}>
          <i className={`bi bi-arrow-clockwise${loading ? " hostinger-spin" : ""}`}></i>
          Yenile
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="hostinger-error">
          <i className="bi bi-exclamation-circle-fill me-2"></i>{error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !status && (
        <div className="hostinger-skeleton-list">
          {[1, 2].map((i) => <div key={i} className="hostinger-skeleton-card" />)}
        </div>
      )}

      {/* Overall status banner */}
      {status && reachableMeta && (
        <>
          <div className="hostinger-overall" style={{ background: reachableMeta.bg, border: `1.5px solid ${reachableMeta.border}` }}>
            <div className="hostinger-overall__left">
              <i className={`bi ${reachableMeta.icon} hostinger-overall__icon`} style={{ color: reachableMeta.color }}></i>
              <div>
                <div className="hostinger-overall__label">Genel Durum</div>
                <div className="hostinger-overall__status" style={{ color: reachableMeta.color }}>{reachableMeta.label}</div>
              </div>
            </div>
            <div className="hostinger-overall__right">
              <div className="hostinger-overall__dur">
                <i className="bi bi-database-fill-check me-1"></i>
                {status.fromCache ? "Önbellekten" : "Canlı sorgu"}
              </div>
              <div className="hostinger-overall__time">
                Kontrol: {formatDateTime(status.checkedAtUtc)} · Sayfa yenileme: {lastRefresh.toLocaleTimeString("tr-TR")}
              </div>
            </div>
          </div>

          {/* VM cards */}
          {status.virtualMachines.length === 0 ? (
            <div className="hostinger-empty">
              <i className="bi bi-hdd-rack"></i>
              <div className="hostinger-empty__title">Sunucu bulunamadı</div>
              <div className="hostinger-empty__desc">Hesapla ilişkili herhangi bir VPS görünmüyor.</div>
            </div>
          ) : (
            <div className="hostinger-vm-list">
              {status.virtualMachines.map((vm) => (
                <VmCard key={vm.id} vm={vm} metrics={status.metrics[vm.id]} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HostingerPage;
